import { useCallback } from 'react';
import { App } from 'antd';
import {
  useEnqueuePrintJobMutation,
  useEnqueueKitchenOrderMutation,
  EnqueuePrintJobRequest,
  EnqueueKitchenOrderPrintRequest,
  EnqueuePrintJobResult,
} from '../services/cloudPrintJobsApi';
import { useAppDispatch } from './redux';
import {
  PrintRetry,
  printDismissed,
  printEnqueued,
  printStarted,
} from '../store/slices/cloudPrintSlice';

/**
 * Cloud print, fire-and-forget. Enqueues the batch and hands progress off to the global
 * <PrintStatusListener/> (mounted once in MainLayout), which raises a non-blocking toast.
 *
 * The caller is NOT held while the ticket reaches the printer: `printJob` resolves as soon as
 * the server accepts the batch. This used to open a centered, non-closable Modal per call site —
 * on a slow connection that parked the user behind a mask for up to 60 s, and in cobro the modal
 * was torn down mid-poll when PaymentModal closed itself, so the result was never shown at all.
 */

// Batch ids only need to be unique within a session; crypto.randomUUID() is unavailable on
// non-secure origins (LAN dev over http), so don't depend on it.
let batchSeq = 0;
const newBatchId = () => `print-${Date.now()}-${++batchSeq}`;

const LABEL_BY_PRINT_TYPE: Record<string, string> = {
  Payment: 'Ticket de pago',
  PreCheck: 'Precuenta',
  KitchenOrder: 'Comanda',
  CashCount: 'Arqueo de caja',
  CashRegisterClosure: 'Corte de caja',
  CashMovement: 'Movimiento de caja',
};

const labelFor = (printType: string) => LABEL_BY_PRINT_TYPE[printType] ?? 'Ticket';

/**
 * Shared enqueue path. Used by useCloudPrint for the initial send and by the toast watcher for
 * "Reintentar" — reusing the same batch id so the retry updates the existing toast in place.
 */
export function useEnqueuePrintBatch() {
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const [enqueuePrintJob] = useEnqueuePrintJobMutation();
  const [enqueueKitchenOrder] = useEnqueueKitchenOrderMutation();

  return useCallback(
    async (
      batchId: string,
      label: string,
      retry: PrintRetry
    ): Promise<EnqueuePrintJobResult | undefined> => {
      dispatch(printStarted({ id: batchId, label, retry, startedAt: Date.now() }));
      try {
        const result =
          retry.kind === 'kitchen'
            ? await enqueueKitchenOrder(retry.request).unwrap()
            : await enqueuePrintJob(retry.request).unwrap();

        result.warnings.forEach(w => message.warning(w));
        if (result.jobIds.length === 0) {
          // Nothing was actually queued (e.g. no printer routed for these items). The warnings
          // above already said why; don't leave an empty toast spinning forever.
          dispatch(printDismissed(batchId));
        } else {
          dispatch(printEnqueued({ id: batchId, jobIds: result.jobIds }));
        }
        return result;
      } catch (err: any) {
        message.error(err?.data?.message || 'Error al enviar a imprimir');
        dispatch(printDismissed(batchId));
        return undefined;
      }
    },
    [dispatch, enqueuePrintJob, enqueueKitchenOrder, message]
  );
}

export function useCloudPrint() {
  const enqueueBatch = useEnqueuePrintBatch();

  const printJob = useCallback(
    (request: EnqueuePrintJobRequest) =>
      enqueueBatch(newBatchId(), labelFor(request.printType), { kind: 'job', request }),
    [enqueueBatch]
  );

  const printKitchenOrder = useCallback(
    (request: EnqueueKitchenOrderPrintRequest) =>
      enqueueBatch(newBatchId(), 'Comanda', { kind: 'kitchen', request }),
    [enqueueBatch]
  );

  return { printJob, printKitchenOrder };
}
